import { trpc, isTRPCClientError } from 'api'
import {
  CustomerPoliciesState,
  CustomerPolicyPatch,
  DEFAULT_REGION_POLICIES,
} from '@ca/common'
import { useSession } from 'hooks/useSession'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useToast } from '@ui/toasts/useToastManager'
import { useTranslation } from 'react-i18next'

type CustomerPolicyItem = {
  key: string
  required: boolean
  revision: string
  acceptedAt: string | null
}

export const useCustomerPolicies = () => {
  const toast = useToast()
  const { t } = useTranslation(['toasts', 'validation'])
  const customer = useSession((s) => s.customer)
  const config = useRemoteConfig()
  const setCustomerPolicies = useSession((s) => s.setCustomerPolicies)
  const regionPolicies = config?.region.policies ?? DEFAULT_REGION_POLICIES
  const customerPolicies = customer?.policies ?? ({} as CustomerPoliciesState)
  const getCustomerPolicyAcceptedAt = (key: string, revision: string) => {
    const customerPolicy = customerPolicies[key]
    if (!customerPolicy || customerPolicy.revision !== revision) {
      return null
    }
    return customerPolicy.acceptedAt
  }
  const policies = regionPolicies.map((policy): CustomerPolicyItem => {
    return {
      key: policy.policy_key,
      required: policy.required,
      revision: policy.policy_revision,
      acceptedAt: getCustomerPolicyAcceptedAt(
        policy.policy_key,
        policy.policy_revision
      ),
    }
  })

  const patchMutation = trpc.customer.policies.patch.useMutation({
    onSuccess: (res) => {
      setCustomerPolicies(res.policies)
    },
    onError: (error) => {
      if (isTRPCClientError(error)) {
        const httpStatus = error.data?.httpStatus
        if (httpStatus && httpStatus >= 500) {
          toast({
            variant: 'error',
            title: t('toasts:error.default.title'),
            text: t('toasts:error.default.desc'),
          })
          return
        }
        if (error.data?.code === 'UNAUTHORIZED') {
          toast({
            variant: 'error',
            title: t('toasts:error.default.title'),
            text: t('toasts:error.unauthorized.desc'),
          })
          return
        }
      }
      toast({
        variant: 'error',
        text: t('validation:confirmation.policies'),
      })
    },
  })

  const isPolicyAccepted = (key: string, revision: string) => {
    return !!getCustomerPolicyAcceptedAt(key, revision)
  }

  const getPolicyAcceptedAt = (key: string, revision: string) => {
    return getCustomerPolicyAcceptedAt(key, revision)
  }

  const patchPolicies = async (patch: CustomerPolicyPatch) => {
    if (Object.keys(patch).length === 0) {
      return true
    }
    try {
      await patchMutation.mutateAsync({
        policies: patch,
      })
      return true
    } catch {
      return false
    }
  }

  return {
    policies,
    isPolicyAccepted,
    getPolicyAcceptedAt,
    patchPolicies,
    isSaving: patchMutation.isPending,
  }
}
